import {
  useState,
  useReducer,
  useContext,
  useCallback,
  createContext,
  useMemo,
  useEffect
} from "react";
import { useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { useDataSaveItem, useDataSaveBatch } from "./useData";
import { hasBatchContent } from "./useData/batch";
import { inheritFromBatch } from "./draft/merge";
import { useGetMemItem } from "./useMemoryDB";
import get from "lodash.get";
import idGen from "../utils/idGen";
import { deriveRow, itemFromRow, sameItem } from "./itemRow";

export const ItemContext = createContext(null);

export function useItemContext() {
  return useContext(ItemContext);
}

const errorReducer = (state, data) => state;
// Stable fallback: a new [] at each call made useSelector re-render the
// component on every redux update.
const noPermissions = [];

// formDirtyRef (the Item's): formDirtyRef.current() tells whether the form
// has changes now (see the freeze of a copy below).
export default function useItem(props, { formDirtyRef } = {}) {
  const params = useParams();
  const { search } = useLocation();
  // A refused save (version conflict) opens the conflict dialog of the Item
  // instead of the error notification.
  // draftScope: the draft the Item's Save sends; a row drafted in another
  // scope is refused (it would commit that draft).
  const saveItem = useDataSaveItem({
    notify: true,
    conflictDialog: true,
    draftScope: props.draftScope
  });
  const saveBatch = useDataSaveBatch({
    notify: true,
    conflictDialog: true,
    draftScope: props.draftScope
  });

  const [itemErrors, setItemErrors] = useReducer(errorReducer, {});

  const copyId = useMemo(
    () => (search ? search.substring(search.indexOf("=") + 1) : null),
    [search]
  );
  const isCopy = useMemo(() => (copyId ? true : false), [copyId]);

  const [isNew, setIsNew] = useState(params?.itemId === "newItem");
  const [prevItemId, setPrevItemId] = useState(props?.itemId || params?.itemId);
  const [itemId, setItemId] = useState(
    isNew ? idGen() : props?.itemId || params?.itemId
  );
  const [itemIdError, setItemIdError] = useState(false);
  const rawItem = useGetMemItem(props.accessor, copyId || prevItemId);

  const [item, setItemValues] = useState(() => {
    // if (rawItem && copyId) {
    //   rawItem.title = "";
    //   rawItem.description = "";
    //   rawItem.refs = {};
    // }
    // return rawItem;
  });

  // The item from the stored row, at each read of it. A re-read of a row
  // that did not change (db.dispatchAll() after a load, a restart of the
  // worker, a notification of the table) keeps the same item: a new one
  // that differs from the form's initial values makes Formik reinitialise
  // the form, discarding its unsaved edits (see itemRow.js).
  // A block gets an _id on each sub-block (deterministic: the same at every
  // read of the same row; a copy, new ones from the id of the new item); an
  // endpoint without prevReference gets its reference as prevReference.
  // The id the form was opened with (not changed by a rename of the item):
  // the sub-block ids of a copy come from it.
  const [openedAs] = useState(itemId);
  useEffect(() => {
    const next = itemFromRow(props.accessor, rawItem, {
      copy: Boolean(copyId),
      id: openedAs
    });
    setItemValues((previous) => {
      if (sameItem(previous, next)) return previous;
      // A copy (or a new item) being edited is not the row it was made
      // from: a save of the source by someone else (or by us elsewhere)
      // must not reinitialise it, and nothing holds the source for it (no
      // row of its own is registered). Clean, it follows the source.
      if (previous && (copyId || isNew) && formDirtyRef?.current?.())
        return previous;
      return next;
    });
    // isNew is read when the row changes, not a reason to derive it again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawItem, copyId, openedAs, props.accessor]);

  // What the item adds to a stored row (deriveRow): the stash of the form
  // compares its row with the stored one through it.
  const fromRow = useCallback(
    (row) => deriveRow(props.accessor, row),
    [props.accessor]
  );

  const hasBelongs = useMemo(() => {
    let count = 0;

    for (const belong of get(item, "refs.belongs") || []) {
      count += (belong.items || []).length;
    }

    return count ? true : false;
  }, [item]);

  // cb(itemId, isNew) once saved; onError(status, error) when not saved
  // (failed, refused on main, or a version conflict: error.type "conflict").
  // extra: { items, deletes, modifiche } to save in the same request as the
  // item (one commit); without it the item is saved alone, as before.
  // baseVersion: the version of the stored row the form was loaded from (none
  // for a new item): the save is refused if someone saved it meanwhile.
  // INVARIANT: it is read from MemoryDB at the Save (rawItem.version), which
  // is the version the form was loaded from only because MemoryDB stays on
  // that row while the form has changes: a colleague's save of it is held
  // out of MemoryDB (the hold, draft/README.md), not written and kept away
  // from the form. Were their row written while the form kept its values,
  // this Save would send their version as its base and overwrite their
  // work without a conflict. A clean form follows the row (reinitialised:
  // its base is the new row).
  // asNew: the row is not stored yet although the form is not a new item (a
  // row created by the draft, e.g. a new variant): sent as the draft sends
  // a created row (isNew, baseVersion 0); cb still gets the form's isNew.
  const onSubmission = useCallback(
    (data, cb, { onError, extra, asNew = false } = {}) => {
      const newValues = data;
      const payload = {
        accessor: props.accessor,
        Id: itemId,
        prevId: prevItemId,
        isNew: isNew || asNew,
        ...(!isNew && {
          baseVersion: asNew ? 0 : Number(rawItem?.version ?? 0)
        }),
        data
      };
      const onSaved = (status, data) => {
        if (status === "OK") {
          setIsNew(false);
          setPrevItemId(itemId);
          setItemValues(newValues);
        }
        cb && cb(itemId, isNew);
      };

      if (hasBatchContent(extra)) {
        saveBatch(
          {
            // A row the draft created that takes fields of the form's row
            // (a new endpoint, the params of the property) takes them now
            items: inheritFromBatch([payload, ...(extra.items ?? [])]),
            deletes: extra.deletes,
            modifiche: extra.modifiche
          },
          onSaved,
          onError
        );
      } else saveItem(payload, onSaved, onError);
    },
    [
      isNew,
      itemId,
      prevItemId,
      props.accessor,
      rawItem?.version,
      saveBatch,
      saveItem
    ]
  );

  // The new item is stored already (a create retried after it went through:
  // the server's "exists" was our own last save, see Item): from now on
  // the form is the stored row's, as after a Save
  const claimSaved = useCallback((id) => {
    setIsNew(false);
    setPrevItemId(id);
  }, []);

  const permissions = {
    write: useSelector(
      (state) =>
        state.user.permissions[state.user.tenant].write || noPermissions
    ),
    expert: useSelector(
      (state) =>
        state.user.permissions[state.user.tenant].expert || noPermissions
    )
  };

  return {
    name: props.name,
    accessor: props.accessor,
    // The draft of the page, saved by the Save of the form (see Item)
    draftScope: props.draftScope,
    permissions,
    item,
    isNew,
    hasBelongs,
    itemId,
    prevItemId,
    itemIdError,
    itemErrors,
    isCopy,
    setItemValues,
    fromRow,
    setItemErrors,
    setItemIdError,
    setItemId,
    onSubmission,
    claimSaved
  };
}
